import http from '@/utils/http';
import { Activity, ActivityFeedItem, Attachment, Board, BoardAccess, Comment, Item, ItemLink, ListRequest, ListResult, Member, MentionComment, Notification, SavedFilter, Status, StatusMetric, Watch } from './types';

// ---- items ----
export const listItems = (req: ListRequest) => http.post<ListResult<Item>>('/items/list', req).then((r) => r.data);
export const getItem = (id: string) => http.get<Item>(`/items/${id}`).then((r) => r.data);
/** Batch-resolve items by id (board-access-gated server-side) — for the search table's epic/parent
 *  columns + ancestor-tree flyout. Unknown / inaccessible ids are silently omitted from the result. */
export const itemsByIds = (ids: string[]) =>
  ids.length === 0 ? Promise.resolve([] as Item[]) : http.post<ListResult<Item>>('/items/by-ids', { ids }).then((r) => r.data.data);
/** Workspace-global set of item labels (for label autocomplete + the filter facet). */
export const listLabels = () => http.get<string[]>('/labels').then((r) => r.data);
export const createItem = (body: Partial<Item>) => http.post<Item>('/items', body).then((r) => r.data);
export const updateItem = (id: string, body: Partial<Item>) => http.put<Item>(`/items/${id}`, body).then((r) => r.data);
export const moveItem = (id: string, status: string, position: number) =>
  http.put<Item>(`/items/${id}/position`, { status, position }).then((r) => r.data);
export const reparentItem = (id: string, parentId: string | null, position = 0) =>
  http.put<Item>(`/items/${id}/parent`, { parent_id: parentId, position }).then((r) => r.data);
/** Links an item to an epic (or unlinks when epicId is null). A narrow write that touches only
 *  epic_id — independent of parent_id nesting — so it never clobbers a concurrent field edit.
 *  With `cascade`, the same link is applied to the item's whole descendant subtree (the behaviour
 *  when an epic is dragged onto a row/card), in one atomic server write. */
export const setItemEpic = (id: string, epicId: string | null, opts?: { cascade?: boolean }) =>
  http
    .put<Item>(`/items/${id}/epic`, { epic_id: epicId, ...(opts?.cascade ? { cascade: true } : {}) })
    .then((r) => r.data);
/** Sets (or clears, when color is null/"") an item's presentation colour. A narrow write that
 *  touches only `color`, so it never clobbers a concurrent field edit. Used by the epic colour
 *  picker — the choice is shared and re-tints that epic's chip in every view. */
export const setItemColor = (id: string, color: string | null) =>
  http.put<Item>(`/items/${id}/color`, { color }).then((r) => r.data);
/** Sets an epic's sidebar-rail ordering key (fractional, like a position). Narrow write touching
 *  only `epic_rank`; the drag-to-reorder handler computes the midpoint between new neighbours. */
export const setItemEpicRank = (id: string, epicRank: number) =>
  http.put<Item>(`/items/${id}/epic-rank`, { epic_rank: epicRank }).then((r) => r.data);
/** Soft-deletes the item + its subtree; returns the removed ids (for undo/restore). */
export const deleteItem = (id: string) =>
  http.delete<{ ids: string[] }>(`/items/${id}`).then((r) => r.data);
/** Reverses a soft-delete for the given ids (undo of a delete / redo of a create). */
export const restoreItems = (ids: string[]) =>
  http.post<{ restored: number }>('/items/restore', { ids }).then((r) => r.data);

// ---- boards ----
export const listBoards = () => http.get<Board[]>('/boards').then((r) => r.data);
export const createBoard = (body: Partial<Board>) => http.post<Board>('/boards', body).then((r) => r.data);
export const updateBoard = (id: string, body: Partial<Board>) => http.put<Board>(`/boards/${id}`, body).then((r) => r.data);
export const deleteBoard = (id: string) => http.delete(`/boards/${id}`).then(() => undefined);
/** Board access config (mode + member grants). Admin-only (app-admin or board admin). */
export const getBoardAccess = (id: string) => http.get<BoardAccess>(`/boards/${id}/access`).then((r) => r.data);
export const saveBoardAccess = (id: string, access: BoardAccess) =>
  http.put<BoardAccess>(`/boards/${id}/access`, access).then((r) => r.data);

// ---- statuses (per-board kanban columns) ----
export const listStatuses = (boardId: string) => http.get<Status[]>(`/boards/${boardId}/statuses`).then((r) => r.data);
/** Per-status average cycle time (days) for the items currently in each column. */
export const boardStatusMetrics = (boardId: string) => http.get<StatusMetric[]>(`/boards/${boardId}/status-metrics`).then((r) => r.data);
export const createStatus = (boardId: string, body: { label: string; color?: string; position?: number }) =>
  http.post<Status>(`/boards/${boardId}/statuses`, body).then((r) => r.data);
/** Update a status. Omit `position` for a pure recolour/rename (order preserved server-side);
 *  include it only for a reorder — so a recolour never writes back a stale position. */
export const updateStatus = (id: string, body: { label: string; color: string; position?: number }) =>
  http.put<Status>(`/statuses/${id}`, body).then((r) => r.data);
/** Deletes a status; its live items are reassigned server-side. Returns the count moved
 *  and the value they were moved to. */
export const deleteStatus = (id: string) =>
  http.delete<{ moved: number; to: string }>(`/statuses/${id}`).then((r) => r.data);

// ---- session ----
/** Legacy password-free email sign-in (used only when OIDC is disabled). */
export const signIn = (email: string, name?: string) =>
  http.post<Member>('/session', { email, name }).then((r) => r.data);
/** OIDC sign-in: the backend derives identity from the verified Bearer token (body ignored),
 *  auto-provisioning the member on first sign-in and applying the admin allowlist. */
export const resolveSession = () => http.post<Member>('/session', {}).then((r) => r.data);

// ---- current user: personal MCP API key (owner-scoped; only you can read/rotate yours) ----
/** Your API key, or "" if none has been minted yet. */
export const getMyApiKey = () => http.get<{ api_key: string }>('/me/api-key').then((r) => r.data.api_key);
/** Mint/rotate your API key and return the new value. */
export const regenerateMyApiKey = () =>
  http.post<{ api_key: string }>('/me/api-key/regenerate', {}).then((r) => r.data.api_key);

// ---- current user preferences (per-member UI prefs, keyed off X-Actor-Id) ----
/** UI preferences for the signed-in member. Open-ended (the server stores an opaque
 *  blob and merges patches), typed here only for the keys the client reads/writes. */
export interface MePreferences {
  /** Keep the item details panel open as a docked side drawer rather than a modal. */
  drawer_pinned?: boolean;
  /** Preferred width (px) of the docked details drawer, set by dragging its left edge. */
  dock_width?: number;
  /** Kanban cards expanded to reveal their sub-items, keyed by board id → open item ids. */
  kanban_expanded?: Record<string, string[]>;
  /** Keep the epic list sidebar open (per member, across boards/views). */
  epic_sidebar_open?: boolean;
  /** Per-board Table column layout, keyed by board id: the ordered column list plus which are
   *  hidden. A legacy bare `string[]` (old visible-only list) is still accepted on read. Absent →
   *  the default layout. Lets each member tailor the table without affecting others. */
  table_columns?: Record<string, { order?: string[]; hidden?: string[] } | string[]>;
  /** Receive email digests for watched items/boards (default true). Toggled in User Settings. */
  email_notifications?: boolean;
  /** Personal saved global searches (name + query + the results-table view), for the global search. */
  saved_searches?: { id: string; name: string; q: string; cols?: string; sort?: string; created_at?: string }[];
  /** Cross-board search results table layout: ordered column list + which are hidden. */
  search_columns?: { order?: string[]; hidden?: string[] };
  /** Colour theme: 'system' follows the OS (default), else an explicit 'light'/'dark'. Synced across devices. */
  theme?: 'system' | 'light' | 'dark';
}
export const getMyPreferences = () => http.get<MePreferences>('/me/preferences').then((r) => r.data);
/** Shallow-merges the patch server-side; returns the full merged preferences. */
export const saveMyPreferences = (patch: MePreferences) =>
  http.put<MePreferences>('/me/preferences', patch).then((r) => r.data);

// ---- web push ----
/** The server's VAPID public key (empty string when push isn't configured). */
export const pushPublicKey = () => http.get<{ public_key: string }>('/me/push/public-key').then((r) => r.data);
/** Register this browser's push subscription for the signed-in member. */
export const savePushSubscription = (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
  http.post('/me/push/subscribe', sub).then(() => undefined);
/** Unregister this browser's push subscription (by endpoint). */
export const removePushSubscription = (endpoint: string) =>
  http.post('/me/push/unsubscribe', { endpoint }).then(() => undefined);

// ---- notifications ----
export const listNotifications = (limit = 50) =>
  http.get<Notification[]>('/me/notifications', { params: { limit } }).then((r) => r.data);
export const unreadNotificationCount = () =>
  http.get<{ unread: number }>('/me/notifications/unread-count').then((r) => r.data.unread);
export const markNotificationRead = (id: string) => http.put(`/notifications/${id}/read`, {}).then(() => undefined);
export const markAllNotificationsRead = () => http.put('/me/notifications/read-all', {}).then(() => undefined);

// Personal activity feed — the "what's relevant to me" inbox behind the notification bell: events on
// items I own / am assigned / PdM / watch, plus @mentions, newest first, over the last `days`.
export const myActivityFeed = (days = 7, limit = 30) =>
  http.get<ActivityFeedItem[]>('/me/activity', { params: { days, limit } }).then((r) => r.data);

// ---- watches (item / board subscriptions) ----
export const listMyWatches = () => http.get<Watch[]>('/me/watches').then((r) => r.data);
export const setWatch = (targetType: 'item' | 'board', targetId: string, watching: boolean) =>
  http.put('/me/watches', { target_type: targetType, target_id: targetId, watching }).then(() => undefined);

// ---- members ----
export const listMembers = () => http.get<Member[]>('/members').then((r) => r.data);
export const createMember = (body: Partial<Member>) => http.post<Member>('/members', body).then((r) => r.data);
export const updateMember = (id: string, body: Partial<Member>) =>
  http.put<Member>(`/members/${id}`, body).then((r) => r.data);
export const deleteMember = (id: string) => http.delete(`/members/${id}`).then(() => undefined);

// ---- comments ----
export const listComments = (itemId: string) => http.get<Comment[]>(`/items/${itemId}/comments`).then((r) => r.data);
export const createComment = (itemId: string, body: { author_id?: string | null; parent_id?: string | null; body: string }) =>
  http.post<Comment>(`/items/${itemId}/comments`, body).then((r) => r.data);
export const updateComment = (id: string, body: string) => http.put<Comment>(`/comments/${id}`, { body }).then((r) => r.data);
export const setCommentResolved = (id: string, resolved: boolean) =>
  http.put<Comment>(`/comments/${id}/resolve`, { resolved }).then((r) => r.data);
export const deleteComment = (id: string) => http.delete(`/comments/${id}`).then(() => undefined);
/** Comments that @mention me, with item context (for the mentions inbox flyout). */
export const listMyMentions = (limit = 50) =>
  http.get<MentionComment[]>('/me/mentions', { params: { limit } }).then((r) => r.data);
/** Toggle a live-scanned WIKI mention (page-body/comment) as read/dismissed — grays it out (resolved),
 *  or reopens it. A dismissed page mention resurfaces on a later edit. */
export const dismissWikiMention = (mentionId: string, resolved: boolean) =>
  http.put(`/me/wiki-mentions/${mentionId}/dismiss`, { resolved }).then(() => undefined);
/** My own comments, newest first, paginated ({ total, data }) — for the bell tab + the dedicated page. */
export const getMyComments = (limit = 50, offset = 0) =>
  http.get<ListResult<MentionComment>>('/me/comments', { params: { limit, offset } }).then((r) => r.data);

// ---- item links (typed, directional, cross-board associations) ----
export const listLinks = (itemId: string) => http.get<ItemLink[]>(`/items/${itemId}/links`).then((r) => r.data);
/** Create a link FROM itemId (source) TO a target (by id or human key) with an outward type. */
export const createLink = (itemId: string, body: { type: string; target_item_id?: string; target_key?: string }) =>
  http.post<unknown>(`/items/${itemId}/links`, body).then((r) => r.data);
export const deleteLink = (id: string) => http.delete(`/links/${id}`).then(() => undefined);

// ---- activity ----
export const listActivity = (itemId: string) => http.get<Activity[]>(`/items/${itemId}/activity`).then((r) => r.data);

// ---- attachments ----
export const listAttachments = (itemId: string) =>
  http.get<Attachment[]>(`/items/${itemId}/attachments`).then((r) => r.data);
export const uploadAttachment = (itemId: string, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return http.post<Attachment>(`/items/${itemId}/attachments`, fd).then((r) => r.data);
};
export const deleteAttachment = (id: string) => http.delete(`/attachments/${id}`).then(() => undefined);
/** Direct download URL (streamed through the API). NOTE: a plain <img>/<a> hitting this URL can't
 *  send the Bearer token, so with OIDC on it 401s — use {@link fetchAttachmentBlob} for display. */
export const attachmentUrl = (id: string) => `${window.config.apiUrl}/attachments/${id}`;
/** Fetch an attachment's bytes THROUGH axios so the auth header rides along (a plain <img>/<a>
 *  can't). Callers wrap the returned blob in an object URL for the thumbnail / open link. */
export const fetchAttachmentBlob = (id: string) =>
  http.get<Blob>(`/attachments/${id}`, { responseType: 'blob' }).then((r) => r.data);

// ---- saved filters ----
export const listFilters = (boardId?: string) =>
  http.get<SavedFilter[]>('/filters', { params: boardId ? { board_id: boardId } : {} }).then((r) => r.data);
export const createFilter = (body: { name: string; board_id?: string | null; query: unknown }) =>
  http.post<SavedFilter>('/filters', body).then((r) => r.data);
export const deleteFilter = (id: string) => http.delete(`/filters/${id}`).then(() => undefined);
